import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import CalendarIntegration from './CalendarIntegration';
import HubSpotIntegration from './HubspotIntegration';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography, IconButton, Tooltip, useTheme, useMediaQuery, Drawer, Paper } from '@mui/material';
import { useState, useEffect } from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HubIcon from '@mui/icons-material/Hub';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'context/AuthContext';
import { useLocation } from 'react-router-dom';
import { useSystemTheme } from 'context/SystemThemeContext';
import { Google } from '@mui/icons-material';
import GoogleAnalyticsIntegration from './GoogleAnalyticsIntegration';

const Integrations = () => {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const [activeTab, setActiveTab] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { currentDomain } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (isMobile) {
      setCollapsed(false);
    } else if (isTablet) {
      setCollapsed(true);
    }
  }, [isMobile, isTablet]);

  const integrations = [
    { id: 'calendar', name: 'Calendars', icon: <CalendarTodayIcon fontSize="small" />, component: <CalendarIntegration /> },
    { id: 'hubspot', name: 'HubSpot', icon: <HubIcon fontSize="small" />, component: <HubSpotIntegration /> },
    { id: 'google-analytics', name: 'Google Analytics', icon: <Google fontSize="small" />, component: <GoogleAnalyticsIntegration /> }
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      const index = integrations.findIndex(i => i.id === tab.toLowerCase());
      if (index !== -1) setActiveTab(index);
    }
  }, [location.search]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleTabSelect = (index) => {
    setActiveTab(index);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const SidebarContent = ({ isMobileDrawer = false }) => (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isDarkMode ? '#060C18' : '#ffffff',
        width: '100%',
        transition: 'background-color 0.3s ease',
      }}
    >
      {isMobileDrawer && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 2,
            borderBottom: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0e0e0'}`,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: isDarkMode ? '#e2e8f0' : undefined }}>
            Integrations
          </Typography>
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon fontSize="small" sx={{ color: isDarkMode ? '#e2e8f0' : undefined }} />
          </IconButton>
        </Box>
      )}

      {!isMobileDrawer && (
        <Box sx={{ 
          padding: 2, 
          borderBottom: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0e0e0'}`,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          {!collapsed && (
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '1rem', 
                fontWeight: 600, 
                color: '#219297' 
              }}
            >
              Integrations
            </Typography>
          )}
          
          <IconButton 
            onClick={() => setCollapsed(!collapsed)} 
            size="small"
            sx={{ 
              ml: !collapsed ? 'auto' : 0,
              color: isDarkMode ? '#e2e8f0' : undefined,
            }}
          >
            {collapsed ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      <List sx={{ flexGrow: 1, width: '100%', p: isMobileDrawer ? 1 : 2 }}>
        {integrations.map((integration, index) => (
          <Tooltip
            title={!isMobileDrawer && collapsed ? integration.name : ''}
            placement="right"
            key={integration.id}
          >
            <ListItemButton
              selected={activeTab === index}
              onClick={() => handleTabSelect(index)}
              sx={{
                borderRadius: '8px',
                margin: '4px',
                paddingY: 1,
                paddingX: !isMobileDrawer && collapsed ? 1 : 2,
                color: isDarkMode ? '#e2e8f0' : undefined,
                '&.Mui-selected': {
                  backgroundColor: '#219297',
                  color: '#fff',
                  '& .MuiListItemIcon-root': { color: '#fff' },
                  '&:hover': {
                    backgroundColor: '#39b1b8',
                    color: '#fff',
                    '& .MuiListItemIcon-root': { color: '#fff' },
                  },
                },
                '&:hover': {
                  backgroundColor: isDarkMode ? '#2d2d3f' : '#39b1b8',
                  color: '#fff',
                  '& .MuiListItemIcon-root': { color: '#fff' },
                },
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: !isMobileDrawer && collapsed ? 'auto' : 38,
                color: activeTab === index ? '#fff' : (isDarkMode ? '#a0aec0' : undefined),
              }}>
                {integration.icon}
              </ListItemIcon>
              {(isMobileDrawer || !collapsed) && (
                <ListItemText
                  primary={integration.name}
                  primaryTypographyProps={{
                    fontSize: isMobileDrawer ? '0.9rem' : '0.8rem',
                    fontWeight: 500,
                    color: activeTab === index ? '#fff' : (isDarkMode ? '#e2e8f0' : undefined),
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </Box>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      
      {isMobile && (
        <Box sx={{ mb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: isDarkMode ? '#060C18' : '#ffffff',
              borderRadius: '12px',
              padding: 2,
              border: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0e0e0'}`,
              transition: 'background-color 0.3s ease',
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, color: isDarkMode ? '#e2e8f0' : undefined }}>
              {integrations[activeTab]?.name || 'Integrations'}
            </Typography>
            <IconButton onClick={handleDrawerToggle}>
              <MenuIcon sx={{ color: isDarkMode ? '#e2e8f0' : undefined }} />
            </IconButton>
          </Paper>
        </Box>
      )}

      <Box display="flex" mt={isMobile ? 1 : 2} sx={{ minHeight: '70vh' }}>
        {!isMobile && (
          <Box
            sx={{
              position: 'sticky',
              top: 90,
              height: 'auto',
              width: collapsed ? 75 : 220,
              minWidth: collapsed ? 75 : 220,
              transition: 'all 0.3s ease',
              borderRight: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0e0e0'}`,
              backgroundColor: isDarkMode ? '#060C18' : '#ffffff',
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              alignItems: collapsed ? 'center' : 'flex-start',
              mr: 2,
            }}
          >
            <SidebarContent />
          </Box>
        )}

        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{ 
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: 280,
                backgroundColor: isDarkMode ? '#060C18' : '#fff',
              } 
            }}
          >
            <SidebarContent isMobileDrawer={true} />
          </Drawer>
        )}

        <Box
          flexGrow={1}
          sx={{
            width: isMobile ? '100%' : 'auto',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 120px)',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              backgroundColor: isDarkMode ? '#060C18' : '#ffffff',
              borderRadius: '12px',
              minHeight: '20vh',
              border: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0e0e0'}`,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}
          >
            {integrations[activeTab]?.component}
          </Paper>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default Integrations;